<template>
  <div id="mng-means">

    <!-- add- / edit-valinta -->
    <div class="flex justify-around pb-4">
      <button
        v-for="mode in modes"
        :key="mode.mode"
        @click="toggleMode(mode.mode)"
        class="btn"
        :class="{ 'btn-empty': mode.mode !== currentMode }"
      >{{ mode.text }}</button>
    </div>

    <!-- keinolista / olemassa olevat keinot (EDIT) -->
    <div v-if="currentMode === 'edit'" class="form-field mt-0 mb-4">
      <label for="is-topic">{{ meanListTitle }}</label>
      <select name="isMean" v-model="isMean" v-on:change="onChange" class="rounded-5">
        <option v-for="(mean, index) in means" :key="index" class="color-black">{{ mean.title }}</option>
      </select>
    </div>

    <!-- poista itemi firestoresta -->
    <div v-if="isMean" class="flex justify-center mb-4">
      <div @click="deleteConfirm = true" class="i-flex align-center txt-center font-8 pointer">
        <span class="color-grey">{{ deleteMeanText }}</span>
        <i class="material-icons ml-1 color-grey-l1">delete</i>
      </div>
    </div>

    <!-- poista itemi firestoresta -modal -->
    <div v-if="deleteConfirm" class="fixed tl-0 br-0 flex flex-center" style="background-color: rgba(0, 0, 0, 0.3);">
      <div class="card i-block px-10 py-14 txt-center">
        <p class="font-10 txt-bold">{{ deleteMeanWarning1 }}</p>
        <button @click.prevent="deleteConfirm = false" class="btn block mx-auto mt-4">{{ cancelText }}</button>
        <p class="mt-4 font-10">{{ deleteMeanWarning2 }}</p>
        <button @click.prevent="confirmDeleteFromDb" class="btn btn-3 block mx-auto mt-8" style="background-color: red;">Poista</button>
        <p>{{ this.deleteConfirmFeedback }}</p>
      </div>
    </div>

    <!-- lomakkeen otsikko -->
    <hr class="border-0 border-t-l1 mb-1">
    <div class="flex justify-between">
      <h2 v-if="currentMode === 'add'" class="mb-5 txt-bold">{{ addMeanTitle }}</h2>
      <h2 v-else class="mb-5 txt-bold">{{ updateMeanTitle }}</h2>
      <i @click="showModal = true" class="material-icons color-grey-l1 font-10 pt-1">{{ helpText }}</i>
    </div>

    <!-- tekstin editoinitohjeet - modal -->
    <portal to="modals" v-if="showModal">
      <modal-html-guide
        :show="showModal"
        @close="showModal = false"
      ></modal-html-guide>
    </portal>

    <form @submit.prevent>

      <!-- kategoriat (lista) -->
      <div class="form-field" :class="{ required: isRequired }">
        <label for="isCategory">{{ categoryNameText }}</label>
        <select name="isCategory" v-model="isCategory" v-on:change="onChangeCategory" class="rounded-5">
          <option v-for="(category, index) in categories" :key="index">{{ category.title }}</option>
        </select>
      </div>

      <!-- keinon nimi -->
      <div class="form-field" :class="{ required: isRequired }">
        <label for="title">{{ meanNameText }}</label>
        <input type="text" name="title" v-model="currentMean.title">
      </div>

      <!-- keinon selite -->
      <div class="form-field" :class="{ required: isRequired }">
        <label for="main-info">{{ meanInfoText }}</label>
        <textarea name="main-info" rows="5" v-model="currentMean.mainInfo"></textarea>
      </div>

      <!-- keinon lyhyt selite -->
      <div class="form-field" :class="{ required: isRequired }">
        <label for="teaser">{{ meanTeaserText }}</label>
        <textarea name="teaser" rows="5" v-model="currentMean.teaser"></textarea>
      </div>

      <!-- ominaisuuden lisäys -->
      <div class="form-field px-6 py-4 mt-6 border-l2 rounded-5 bg-grey-l5">
        <!-- tiedot -->
        <label for="attr-title">{{ attrNameText }}</label>
        <input type="text" name="attr-title" v-model="attrTitle">
        <label for="attr-text" class="mt-2">{{ attrInfoText }}</label>
        <textarea name="attr-text" rows="3" v-model="attrText"></textarea>
        <!-- kuvan lisäys -->
        <div class="relative flex align-center mt-1">
          <input
            type="file"
            @change="onFileChange"
            style="display: none;"
            ref="inputFileSelect">
          <button @click="$refs.inputFileSelect.click()" class="btn-empty btn-2- mr-2">{{ addImageButtonText }}</button>
        </div>

        <!-- lisätyt kuvat (+ kuvatekstit) -->
        <div v-for="(group, index) in attrSubGroups" :key="index" class="mt-1 txt-bold flex justify-between align-center">
          <div class="flex-1">
            <p>{{ group.filename }}</p>
            <input v-model="group.imageText" type="text" v-bind:placeholder="captionPlaceholder" class="mt-1">
          </div>
          <i @click="$delete(attrSubGroups, index)" class="material-icons ml-2 color-grey">{{ deleteAttrButtonText }}</i>
        </div>

        <!-- feedback ja "liitä"-nappi -->
        <p v-if="attrFeedback">{{ this.attrFeedback }}</p>
        <button @click.prevent="addAttr" class="btn mt-4">{{ attachButtonText }}</button>
      </div>

      <!-- fieldit (lista) -->
      <div class="form-field" v-if="currentMean.attrs">
        <div v-for="(attr, index) in currentMean.attrs" :key="index" class="flex justify-between align-center mb-4">
          <div class="relative oflow-hidden mr-2 flex-1">
            <h3 class="txt-bold font-11">{{ attr.title }}</h3>
            <p class="w-full ellipsis font-9">{{ attr.text }}</p>
            <div v-for="(pair, index) in attr.imgTextPairs" :key="index" class="mt-1">
              <p class="color-grey">{{imageText}} {{index+1}}: <span class="color-theme txt-bold">{{pair.filename}}</span></p>
              <p v-if="pair.imageText" class="color-grey">{{ captionText }}: <span class="color-black">{{pair.imageText}}</span></p>
            </div>
          </div>
          <i class="material-icons color-grey" @click="editAttr(index)">{{ editAttrButtonText }}</i>
          <i class="material-icons color-grey" @click="deleteAttr(index)">{{ deleteAttrButtonText }}</i>
        </div>
      </div>

      <p v-if="feedback" class="txt-center">{{ this.feedback }}</p>
      <button @click.self="addToDatabase" class="btn btn-3 block mx-auto mt-6">{{this.addOrEdit}}</button>

    </form>

  </div>
</template>

<script>
// ===================================================================================================================
import db from '@/firebase/init'
import firebase from 'firebase'
import slugify from 'slugify'
import modalHtmlGuide from '../components/modals/modalHtmlGuide'
import uuidv4 from 'uuid/v4'

export default {
  name: 'mngMeans',

  components: {
    modalHtmlGuide
  },

  data () {
    return {
      meanDB: 'keinot',
      categoryDB: 'kategoriat',

      deleteTimeout: 2000,
      updateTimeout: 2500,
      addTimeout: 2500,

      imageFolder: 'attrs',
      thumbS_suffix: '_thumb@200',
      thumbM_suffix: '_thumb@420',

      // Kaikki nämä allaolevat tekstit olisi hyvä paketoida yhteen olioon.
      // Sitten, jos tehdään jotain sivua useammalle kielelle,
      // niin meillä olis eri oliot eri kielille ja kielivalinta vaan vaihtaa
      // oikean teksti-olion käyttöön ja hommahan on nättiä.
      addFeedback: 'kohde lisätty tietokantaan',
      updateFeedback: 'tiedot lisätty tietokantaan',
      requiredFeedback: 'täytä tarvittavat kentät',
      deleteFeedback: 'Keino poistettu tietokannasta',

      meanListTitle: 'tallennetut keinot',
      deleteMeanText: 'Poista keino tietokannasta',
      deleteMeanWarning1: 'Oletko täysin varma että haluat jatkaa?',
      deleteMeanWarning2: 'Tehdyt muutokset eivät ole peruttavissa',
      cancelText: 'Peruuta',
      addMeanTitle: 'Keinon lisäys',
      updateMeanTitle: 'Keinon muokkaus',
      helpText: 'help',
      categoryNameText: 'kategorian nimi',
      meanNameText: 'keinon nimi',
      meanInfoText: 'keinon selite',
      meanTeaserText: 'keinon lyhyt selite',
      attrNameText: 'ominaisuus',
      attrInfoText: 'selite',
      addImageButtonText: 'lisää kuva',
      deleteAttrButtonText: 'delete',
      editAttrButtonText: 'edit',
      attachButtonText: 'liitä',
      imageText: 'kuva',
      captionText: 'kuvateksti',
      captionPlaceholder: 'kuvateksti...',

      currentMean: this.createMeanObject(),
      modes: [
        { mode: 'add', text: 'lisää' },
        { mode: 'edit', text: 'muokkaa' }
      ],
      currentMode: 'add',
      categories: [], // from created()
      isCategory: null, // from <select>
      means: [], // from created()
      isMean: null, // from <select>

      attrTitle: null, // otsikko
      attrText: null, // selite
      attrSubGroups: [], // kuva + kuvateksti -pari

      attrFeedback: null,
      feedback: null,
      isRequired: false,
      deleteConfirm: false, // varmistus: itemin deletointi kannasta
      deleteConfirmFeedback: null,

      deleteList: [], // buffer for image urls to be deleted

      showModal: false // tekstin editointiohjeet
    }
  },

  created () {
    this.getMeans()
    this.getCategories()
  },

  computed: {
    addOrEdit () { // <form>:n buttonin teksti
      if (this.isMean) {
        return 'muokkaa'
      } else {
        return 'lisää'
      }
    }
  },

  methods: {
    getMeans () { // for created()
      this.means = []
      db.collection(this.meanDB).get().then(snapshot => {
        snapshot.docs.forEach(doc => {
          let keino = doc.data()
          keino.id = doc.id // tarvitaanko?
          this.means.push(keino)
        })
      })
    },

    getCategories () { // for created()
      this.categories = []
      db.collection(this.categoryDB).get().then(snapshot => {
        snapshot.docs.forEach(doc => {
          let category = doc.data()
          category.id = doc.id
          this.categories.push(category)
        })
      })
    },

    toggleMode (mode) {
      this.clearFields()
      this.getMeans()
      this.getCategories()
      this.currentMode = mode
    },

    onChange () { // when <select> triggers
      if (this.isMean) { // Q: onko turha tarkastelu?
        let current = this.means.find(mean => {
          return mean.title === this.isMean
        })

        this.currentMean = this.createMeanObject(current)

        this.isCategory = current.categoryTitle
      }
    },

    onChangeCategory () { // when <select> triggers
      if (this.isCategory) {
        let current = this.categories.find(category => {
          return category.title === this.isCategory
        })

        this.currentMean.ref = current.class
        this.currentMean.categoryTitle = current.title
        this.currentMean.categoryIcon = current.icon
      }
    },

    addAttr () { // lisää fields:[] :iin
      if (this.attrTitle && this.attrText) {
        this.attrFeedback = null
        this.currentMean.attrs.push({
          title: this.attrTitle,
          text: this.attrText,
          imgTextPairs: this.attrSubGroups
        })

        this.attrSubGroups.forEach(attrSG => {
          this.deleteList.splice(this.deleteList.findIndex(el => {
            return el.file === attrSG.image
          }), 1)
          this.deleteList.splice(this.deleteList.findIndex(el => {
            return el.file === attrSG.thumbM
          }), 1)
          this.deleteList.splice(this.deleteList.findIndex(el => {
            return el.file === attrSG.thumbS
          }), 1)
        })

        this.attrTitle = null
        this.attrText = null
        this.attrSubGroups = []
      } else {
        this.attrFeedback = this.requiredFeedback
      }
      this.$refs.inputFileSelect.value = ''
    },

    deleteAttr (index) {
      let self = this

      const promises = this.currentMean.attrs[index].imgTextPairs.map(
        img => new Promise(resolve => {
          if (img.image && typeof img.image === 'string') {
            self.deleteList.push({ file: img.image, folder: self.imageFolder })
          }
          if (img.thumbM && typeof img.thumbM === 'string') {
            self.deleteList.push({ file: img.thumbM, folder: self.imageFolder })
          }
          if (img.thumbS && typeof img.thumbS === 'string') {
            self.deleteList.push({ file: img.thumbS, folder: self.imageFolder })
          }
          resolve()
        })
      )

      Promise.all(promises).then(() => {
        self.$delete(self.currentMean.attrs, index)
      })
    },

    editAttr (index) {
      this.attrTitle = this.currentMean.attrs[index].title
      this.attrText = this.currentMean.attrs[index].text
      this.attrSubGroups = this.currentMean.attrs[index].imgTextPairs

      this.deleteAttr(index)
    },

    onFileChange (event) { // kun input(type=file) ottaa uuden filen vastaan
      this.attrSubGroups.push({
        image: event.target.files[0],
        imageText: null,
        filename: event.target.files[0].name
      })
    },

    confirmDeleteFromDb () {
      let toBeDeleted = this.means.find(mean => {
        return mean.title === this.isMean
      }).id

      let self = this

      this.currentMean.attrs.forEach(attr => {
        attr.imgTextPairs.forEach(img => {
          if (img.image && typeof img.image === 'string') {
            self.deleteList.push({ file: img.image, folder: self.imageFolder })
          }
          if (img.thumbM && typeof img.thumbM === 'string') {
            self.deleteList.push({ file: img.thumbM, folder: self.imageFolder })
          }
          if (img.thumbS && typeof img.thumbS === 'string') {
            self.deleteList.push({ file: img.thumbS, folder: self.imageFolder })
          }
        })
      })

      db.collection(this.meanDB).doc(toBeDeleted).delete()
        .then(() => {
          this.deleteConfirmFeedback = this.deleteFeedback

          this.clearFields()
          this.getMeans()
          this.getCategories()

          let deletePromises = []
          if (self.deleteList.length > 0) {
            deletePromises = self.deleteList.map(filu => new Promise((resolve, reject) => {
              self.deleteImage(filu.file, filu.folder, resolve)
            }))
          }
          Promise.all(deletePromises).then(() => {
            self.deleteList = []
          })

          setTimeout(() => {
            this.deleteConfirm = false
            this.deleteConfirmFeedback = null
            this.isMean = null
            this.$store.dispatch('changeUpdateState', 'means')
          }, this.deleteTimeout)
        })
    },

    uploadImage (file, ind, jnd, title, resolve) {
      const ref = firebase.storage().ref(this.imageFolder)
      let name1 = file.name.split('.')[0]
      name1 = name1 + '_' + uuidv4()
      const name2 = file.name.split('.').pop()
      const name = name1 + '.' + name2
      const metadata = { contentType: file.type }
      const task = ref.child(name).put(file, metadata)

      let self = this

      task
        .then(snapshot => snapshot.ref.getDownloadURL())
        .then(url => {
          var filename = url.split(name)
          self.currentMean.attrs[ind].imgTextPairs[jnd].image = url
          if (name2 !== 'svg') {
            self.currentMean.attrs[ind].imgTextPairs[jnd].thumbS = filename[0] + name1 + self.thumbS_suffix + '.png' + filename[1]
            self.currentMean.attrs[ind].imgTextPairs[jnd].thumbM = filename[0] + name1 + self.thumbM_suffix + '.png' + filename[1]
          } else {
            self.currentMean.attrs[ind].imgTextPairs[jnd].thumbS = null
            self.currentMean.attrs[ind].imgTextPairs[jnd].thumbM = null
          }
          resolve()
        })
    },

    deleteImage (file, folder, resolve) {
      const name = file.split('%2F')[1].split('?')[0]
      const ref = firebase.storage().ref(folder)
      const task = ref.child(name).delete()

      task
        .then(() => {
          resolve()
        })
        .catch(err => {
          console.error('ERROR: ', err)
          resolve()
        })
    },

    uploadAttr (attr, ind, title, resolve) {
      var self = this

      const promises = attr.map((img, jnd) => new Promise((resolve, reject) => {
        if (typeof img.image === 'string') {
          // image already in storage
          resolve()
        } else {
          // adding new image to storage
          self.uploadImage(img.image, ind, jnd, title, resolve)
        }
      }))

      Promise.all(promises).then(() => {
        resolve()
      })
    },

    addToDatabase () { // lisää / päivitä kohde tietokantaan
      if (this.currentMean.title && this.currentMean.mainInfo) {
        this.feedback = null
        this.isRequired = false
        this.currentMean.slug = slugify(this.currentMean.title, {
          replacement: '-',
          remove: /[$*_+~.()'"!\-:@]/g,
          lower: true
        })

        let self = this
        let deletePromises = []
        if (self.deleteList.length > 0) {
          deletePromises = self.deleteList.map(filu => new Promise((resolve, reject) => {
            self.deleteImage(filu.file, filu.folder, resolve)
          }))
        }
        Promise.all(deletePromises).then(() => {
          self.deleteList = []
          const promises = this.currentMean.attrs.map((filu, ind) => new Promise((resolve, reject) => {
            self.uploadAttr(filu.imgTextPairs, ind, self.currentMean.slug, resolve)
          }))
          Promise.all(promises).then(() => {
            if (self.isMean) {
              // muokkaa vanhaa
              db.collection(self.meanDB).doc(self.currentMean.id).update(self.currentMean)
                .then(() => {
                  self.clearFields()
                  self.feedback = self.updateFeedback
                  self.isRequired = false

                  self.getMeans() // update means array
                  self.getCategories()

                  setTimeout(() => {
                    self.feedback = null
                    self.$store.dispatch('changeUpdateState', 'means')
                  }, self.updateTimeout)
                })
            } else {
              // lisää uusi
              db.collection(self.meanDB).add(self.currentMean)
                .then(() => {
                  self.clearFields()
                  self.feedback = self.addFeedback
                  self.isRequired = false

                  self.getMeans() // update means array
                  self.getCategories()

                  setTimeout(() => {
                    self.feedback = null
                    self.$store.dispatch('changeUpdateState', 'means')
                  }, self.addTimeout)
                })
            }
          })
        })
      } else {
        this.feedback = this.requiredFeedback
        this.isRequired = true
      }
    },

    createMeanObject (dbObj) {
      let newMean = {
        id: null,
        ref: null,
        title: null,
        mainInfo: null,
        teaser: null,
        attrs: [],
        slug: null,
        categoryTitle: null,
        categoryIcon: null
      }
      if (dbObj) {
        newMean.id = dbObj.id
        newMean.ref = dbObj.ref
        newMean.title = dbObj.title
        newMean.mainInfo = dbObj.mainInfo
        newMean.teaser = dbObj.teaser
        newMean.attrs = dbObj.attrs || []
        newMean.slug = dbObj.slug
        newMean.categoryTitle = dbObj.categoryTitle
        newMean.categoryIcon = dbObj.categoryIcon
      }
      return newMean
    },

    clearFields () {
      this.currentMean = this.createMeanObject()

      this.attrTitle = null
      this.attrText = null

      this.attrSubGroups = []

      // clear form fields
      this.isMean = null // tallennetut keinot -valikon valinta
      this.isCategory = null // kategoria-valikon valinta
    }
  }
}
</script>

<style lang="scss">
// ===================================================================================================================
</style>
